/**
 * Payments and aging (Phase 21 / Task 21.7d): small pure helpers, kept out of the components so
 * the tests can pin them. The API checks every rule again (21.6a, 21.6b).
 */
import { parseDollarsToCents } from '@lodex-pro/shared/money';
import type {
  AgingBucket,
  DueClass,
  Payment,
  PaymentMethod,
  RemindOverdueResponse,
} from './billing-hooks';
import { money } from './invoice-format';
import { priceError } from './rate-card-draft';

export const PAYMENT_METHOD_LABELS: Readonly<Record<PaymentMethod, string>> = {
  CHECK: 'Check',
  ACH: 'ACH',
  WIRE: 'Wire',
  CARD_OFFLINE: 'Card (taken offline)',
  OTHER: 'Other',
};

/**
 * A payment's reference column: why for a reversal; Stripe's id in its own case for a payment
 * Stripe settled (Stripe's search is case-sensitive, OPEN-223); else the typed reference.
 */
export function paymentRefText(
  p: Pick<Payment, 'kind' | 'reason' | 'reference' | 'processor' | 'processorRef'>,
): string {
  if (p.kind === 'REVERSAL') return p.reason ?? '';
  if (p.processor === 'STRIPE' && p.processorRef !== null) return p.processorRef;
  return p.reference ?? '—';
}

export const AGING_BUCKET_LABELS: Readonly<Record<AgingBucket, string>> = {
  CURRENT: 'Current',
  D1_30: '1–30 days',
  D31_60: '31–60 days',
  D61_90: '61–90 days',
  D90_PLUS: 'Over 90 days',
};

export const DUE_CLASS_LABELS: Readonly<Record<DueClass, string>> = {
  OVERDUE: 'Overdue',
  DUE_THIS_WEEK: 'Due this week',
  DUE_LATER: 'Due later',
};

export interface PaymentDraft {
  readonly amount: string;
  readonly paymentDate: string;
}

/**
 * What is wrong with a payment before it is sent, as the API would refuse it: dollars to whole
 * cents, more than $0, no more than the balance (no overpayment in v1), dated from the invoice's
 * issue date up to today in the billing time zone. Empty when it can be recorded.
 */
export function paymentErrors(
  d: PaymentDraft,
  ctx: { balanceCents: number; issueDate: string | null; today: string },
): { amount?: string; paymentDate?: string } {
  const out: { amount?: string; paymentDate?: string } = {};
  const priceProblem = priceError(d.amount);
  if (priceProblem !== null) {
    out.amount = priceProblem;
  } else {
    const p = parseDollarsToCents(d.amount);
    if (p.ok && p.cents === 0) out.amount = 'Cannot be $0';
    else if (p.ok && p.cents > ctx.balanceCents) {
      out.amount = `More than the balance (${money(ctx.balanceCents)})`;
    }
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.paymentDate)) out.paymentDate = 'Choose the day it was paid';
  else if (d.paymentDate > ctx.today) out.paymentDate = 'Cannot be after today';
  else if (ctx.issueDate !== null && d.paymentDate < ctx.issueDate) {
    out.paymentDate = `Cannot be before the invoice was issued (${ctx.issueDate})`;
  }
  return out;
}

/** The result of one remind-overdue click, in words, with what is left. */
export function remindSummary(r: RemindOverdueResponse): string {
  const parts = [`${String(r.counts.sent)} reminder${r.counts.sent === 1 ? '' : 's'} sent`];
  if (r.counts.failed > 0) parts.push(`${String(r.counts.failed)} failed`);
  if (r.counts.skipped > 0) parts.push(`${String(r.counts.skipped)} skipped`);
  if (r.remaining > 0) parts.push(`${String(r.remaining)} more to send`);
  return `${parts.join(', ')}.`;
}

/** "Oct 13, 2026, 2:14 PM CDT" in the billing time zone (never UTC on screen, DEC-004). */
export function localStamp(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(iso));
}
